import { Flex } from "@chakra-ui/layout";
import { Input, Textarea, Box } from "@chakra-ui/react";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Node } from "reactflow";
import { ZodError, z } from "zod";

import { Button } from "@/design/components/button";
import {
  FormControl,
  FormErrorIcon,
  FormErrorMessage,
  FormLabel,
} from "@/design/components/form";
import {
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Modal,
  ModalBody,
} from "@/design/components/modal";
import { triggerAutoSave } from "@/features/workflow-studio/redux";
import { useAppDispatch, useAppSelector } from "@/reduxHooks.ts";
import { closeModal } from "@/slices/modal-slice";

import { useRenameWorkflowVersionMutation } from "../../api";

// Define validation schema
const schema = z.object({
  versionName: z
    .string()
    .max(100, { message: "Version name must be at most 100 characters long" })
    .refine((data) => data.trim() !== "", {
      message: "Version name cannot be empty",
    }),
});

const RenameVersionModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const [renameWorkflowVersion, { isLoading }] =
    useRenameWorkflowVersionMutation();
  const { modalProps } = useAppSelector((state) => state.rootReducer.modals);

  const [formData, setFormData] = useState<{
    versionName: string;
  }>({
    versionName: modalProps?.versionName || "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const validatedData = schema.parse(formData);
      setErrors({});
      const result = await renameWorkflowVersion({
        workflowId: modalProps?.workflowId,
        wfRunId: modalProps?.wfRunId,
        body: { description: validatedData.versionName },
      });
      if (result.data) {
        dispatch(closeModal());
      }
    } catch (error) {
      if (error instanceof ZodError) {
        const fieldErrors: { [key: string]: string } = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            fieldErrors[err.path[0]] = err.message;
          }
        });
        setErrors(fieldErrors);
      }
    }
  };

  const onClose = () => {
    dispatch(closeModal());
  };

  return (
    <Modal isOpen={true} isCentered onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Flow Details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleSubmit} className="!px-5 gap-4 flex flex-col">
            <FormControl isRequired isInvalid={!!errors.versionName}>
              <FormLabel>Version Name</FormLabel>
              <Input
                className="disabled:bg-gray-300 disabled:text-gray-900"
                disabled={isLoading}
                name="versionName"
                onChange={handleInputChange}
                type="text"
                value={formData.versionName}
              />
              <FormErrorMessage>
                <FormErrorIcon />
                {errors.versionName}
              </FormErrorMessage>
            </FormControl>
            <Flex justifyContent={"flex-end"} gap={3}>
              <Button
                onClick={onClose}
                variant="outline"
                size="sm"
                colorScheme="dark"
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                isLoading={isLoading}
                type="submit"
                size="sm"
                colorScheme="dark"
              >
                Save
              </Button>
            </Flex>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default RenameVersionModal;
